import { combineQueries } from 'shared/utils/queryBuilder';
import { dimensionIgnoresCasing } from 'shared/utils/ignoreDimensionCasing';

/**
 * Tree navigation for nested-table results: ordered dimensions define levels.
 * Scalar dimensions = one level each. Path segment dimensions (urlPathSegment / type pathSegment)
 * repeat with increasing index until a non-trailing-slash value completes the path.
 */

export function isPathSegmentDimension(d) {
  return d?.type === 'pathSegment';
}

function buildDimensionPlan(dimensions) {
  return (dimensions || []).map(dim => ({
    dim,
    kind: isPathSegmentDimension(dim) ? 'pathSegment' : 'scalar',
  }));
}

function pathSqlIndexForTrailEntry(trail, dimName, entryIndex) {
  const t = trail[entryIndex];
  if (t.pathSqlIndex != null && t.pathSqlIndex > 0) {
    return t.pathSqlIndex;
  }
  let n = 0;
  for (let i = 0; i <= entryIndex; i += 1) {
    const e = trail[i];
    if (e.name === dimName && e.kind === 'pathSegment') {
      n += 1;
    }
  }
  return n;
}

/**
 * Apply a consumed trail against the plan.
 * Trail entries become baseQuery filters (path segments include URLPathHierarchy index).
 */
export function consumeTrail(plan, trail) {
  const baseQueryRules = [];
  let pi = 0;
  let ti = 0;

  while (ti < trail.length) {
    if (pi >= plan.length) {
      throw new Error('Trail is longer than the nested dimension plan');
    }
    const seg = plan[pi];
    const t = trail[ti];
    const fieldKey = seg.dim.name;

    if (seg.kind === 'scalar') {
      if (t.name !== seg.dim.name || t.kind !== 'scalar') {
        throw new Error('Nested trail does not match dimension plan (scalar)');
      }
      baseQueryRules.push({
        field: fieldKey,
        name: fieldKey,
        operator: '=',
        value: 'rawValue' in t ? t.rawValue : t.value,
        ...(dimensionIgnoresCasing(seg.dim) ? { ignoreCasing: true } : {}),
      });
      pi += 1;
      ti += 1;
    } else if (t.name !== seg.dim.name || t.kind !== 'pathSegment') {
      throw new Error('Nested trail does not match dimension plan (pathSegment)');
    } else {
      baseQueryRules.push({
        field: 'pagePathSegment',
        operator: '=',
        value: t.value,
        index: pathSqlIndexForTrailEntry(trail, fieldKey, ti),
        ...(dimensionIgnoresCasing(seg.dim) ? { ignoreCasing: true } : {}),
      });
      if ((t.value || '').endsWith('/')) {
        ti += 1;
      } else {
        pi += 1;
        ti += 1;
      }
    }
  }

  return {
    trailBaseQuery: { combinator: 'and', rules: baseQueryRules },
    pi,
  };
}

/** Count trailing consecutive path trail entries for this dimension name */
export function pathDepthAtEndOfTrail(trail, dimName) {
  let n = 0;
  for (let i = trail.length - 1; i >= 0; i -= 1) {
    const t = trail[i];
    if (t.name === dimName && t.kind === 'pathSegment') {
      n += 1;
    } else {
      break;
    }
  }
  return n;
}

function countPathTrailEntries(trail, dimName) {
  return (trail || []).filter(t => t.name === dimName && t.kind === 'pathSegment').length;
}

/**
 * ClickHouse requires every HAVING identifier to exist in SELECT/GROUP BY.
 * Include ancestors in GROUP BY; path column uses one index derived from trail.
 */
function pathSqlIndexForQuery(plan, pi, trail, pathDimName) {
  const jPath = plan.findIndex(s => s.kind === 'pathSegment' && s.dim.name === pathDimName);
  if (jPath < 0) {
    return 1;
  }
  if (pi === jPath) {
    return pathDepthAtEndOfTrail(trail || [], pathDimName) + 1;
  }
  if (pi > jPath) {
    const pathEntries = (trail || []).filter(t => t.name === pathDimName && t.kind === 'pathSegment');
    const stored = pathEntries.map(t => t.pathSqlIndex).filter(n => n != null && n > 0);
    if (stored.length) {
      return Math.max(...stored);
    }
    return Math.max(1, countPathTrailEntries(trail, pathDimName));
  }
  return 1;
}

function buildDimensionsInputForPi(plan, pi, trail) {
  const dimensionsInput = [];
  for (let j = 0; j <= pi; j += 1) {
    const seg = plan[j];
    if (seg.kind === 'scalar') {
      dimensionsInput.push(seg.dim);
    } else if (!dimensionsInput.some(d => d.name === seg.dim.name)) {
      dimensionsInput.push(seg.dim);
    }
  }

  const pathIndexByName = {};
  for (const d of dimensionsInput) {
    if (isPathSegmentDimension(d)) {
      pathIndexByName[d.name] = pathSqlIndexForQuery(plan, pi, trail, d.name);
    }
  }

  return { dimensionsInput, pathIndexByName };
}

/**
 * Spec to load direct children of the node whose complete trail is `trail` (including that row's value).
 * Returns null if there are no deeper levels.
 */
export function getNextLevelFetchSpec(dimensions, trail) {
  const plan = buildDimensionPlan(dimensions);
  if (!plan.length) {
    return null;
  }

  const { trailBaseQuery, pi } = consumeTrail(plan, trail || []);
  if (pi >= plan.length) {
    return null;
  }

  const { dimensionsInput, pathIndexByName } = buildDimensionsInputForPi(plan, pi, trail || []);
  const seg = plan[pi];

  return {
    trailBaseQuery,
    dimensionsInput,
    pathIndexByName,
    activeDim: seg.dim,
    activeKind: seg.kind,
  };
}

function toDimensionPayloads(spec) {
  return spec.dimensionsInput.map(d => ({
    ...d,
    index: spec.pathIndexByName[d.name] || 1,
  }));
}

/**
 * GraphQL / API dimension payloads for the first nested-table query (tree root).
 */
export function buildNestedTableRootDimensionsInput(resolvedDimensions) {
  const spec = getNextLevelFetchSpec(resolvedDimensions, []);
  if (!spec) {
    return [];
  }
  return toDimensionPayloads(spec);
}

export function getQueryDimensions({ dimensions = [], nestedTable, switchDimensions, activeDimension }) {
  if (nestedTable && dimensions.length) {
    const rootDims = buildNestedTableRootDimensionsInput(dimensions);
    if (rootDims.length) {
      return rootDims;
    }
  }
  if (switchDimensions && activeDimension) {
    return [activeDimension];
  }
  return dimensions;
}

export function getDimensionFilters(dimensions = []) {
  return dimensions.filter(d => d.filter).map(d => d.filter);
}

export function rowCanExpand(dimensions, trail) {
  return getNextLevelFetchSpec(dimensions, trail) != null;
}

export function buildNestedQueryVariables(baseInput, dimensions, rowCompleteTrail) {
  const spec = getNextLevelFetchSpec(dimensions, rowCompleteTrail);
  if (!spec) {
    return null;
  }

  const trailBaseQuery = spec.trailBaseQuery.rules.length > 0
    ? spec.trailBaseQuery
    : undefined;

  return {
    input: {
      ...baseInput,
      dimensions: toDimensionPayloads(spec),
      baseQuery: combineQueries(
        baseInput.baseQuery,
        trailBaseQuery,
        ...getDimensionFilters(spec.activeDim ? [spec.activeDim] : []),
      ),
    },
  };
}
